import { useRef, useState, useEffect } from 'react';
import PricingCTA from './PricingCTA';

const Phantom = () => {
  const heroVideoRef = useRef(null);
  const [heavyTrafficUrl, setHeavyTrafficUrl] = useState(null);

  useEffect(() => {
    fetch('/videos/heavy-traffic-1.mp4')
      .then(response => response.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        setHeavyTrafficUrl(url);
      })
      .catch(error => console.error('Error loading video:', error));
  }, []);

  return (
    <div className="absolute inset-0 w-full bg-black overflow-y-auto">
      {/* Hero Section */}
      <div className="h-screen relative">
        <div className="absolute inset-0 bg-black/60 z-10" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent z-10" />
        <video
          ref={heroVideoRef}
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => console.error('Video error:', e)}
        >
          <source src="/videos/phantom.mp4" type="video/mp4" />
        </video>

        <div className="relative z-20 container mx-auto px-4 pt-32">
          <h1 className="text-4xl md:text-6xl text-white font-light tracking-wider mb-6 drop-shadow-lg">
            Real-Time Traffic Intelligence
          </h1>
          <p className="text-xl md:text-2xl text-white/90 font-light tracking-wide max-w-2xl drop-shadow-lg">
            Find the most efficient routes in real-time. The only system that sees traffic as it happens.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative bg-black/95 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Live Detection Demo */}
            <div className="mb-24">
              <h2 className="text-3xl md:text-4xl text-white font-light tracking-wider mb-6">
                Live Traffic Analysis
              </h2>
              <p className="text-lg text-white/80 font-light tracking-wide mb-12 max-w-3xl">
                While others rely on delayed GPS data, our proprietary network helps your fleet avoid traffic before it impacts your routes. Every vehicle, always on the most efficient path possible.
              </p>

              {/* Demo Videos */}
              <div className="grid md:grid-cols-2 gap-8 mb-16">
                {/* Heavy Traffic */}
                <div className="bg-white/5 rounded-lg overflow-hidden">
                  <div className="aspect-video">
                    <video
                      controls
                      className="w-full h-full object-contain"
                      onError={(e) => console.error('Light traffic video error:', e)}
                    >
                      <source
                        src="/videos/heavy-traffic-web.mp4"
                        type="video/mp4"
                        onError={(e) => console.error('Source error:', e)}
                      />
                      <p className="text-white/70 p-4">Video failed to load</p>
                    </video>
                  </div>
                  <div className="p-4">
                    <h3 className="text-white font-light text-lg mb-2">Heavy Traffic Detection</h3>
                    <p className="text-white/70 text-sm">Real-time analysis of congested traffic patterns</p>
                  </div>
                </div>

                {/* Light Traffic */}
                <div className="bg-white/5 rounded-lg overflow-hidden">
                  <div className="aspect-video">
                    <video
                      controls
                      className="w-full h-full object-contain"
                      onError={(e) => console.error('Light traffic video error:', e)}
                    >
                      <source
                        src="/videos/traffic.mp4"
                        type="video/mp4"
                        onError={(e) => console.error('Source error:', e)}
                      />
                      <p className="text-white/70 p-4">Video failed to load</p>
                    </video>
                  </div>
                  <div className="p-4">
                    <h3 className="text-white font-light text-lg mb-2">Normal Flow Detection</h3>
                    <p className="text-white/70 text-sm">Baseline traffic monitoring and analysis</p>
                  </div>
                </div>
              </div>

              {/* Key Metrics */}
              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-white/5 rounded-lg p-8 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/10 rounded-full -mr-16 -mt-16" />
                  <h3 className="text-3xl text-white font-light mb-3">Exclusive</h3>
                  <p className="text-white/80 font-light relative z-10">Only source of real-time traffic data</p>
                </div>
                <div className="bg-white/5 rounded-lg p-8 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full -mr-16 -mt-16" />
                  <h3 className="text-3xl text-white font-light mb-3">Real-Time</h3>
                  <p className="text-white/80 font-light relative z-10">Not minutes-old GPS data</p>
                </div>
                <div className="bg-white/5 rounded-lg p-8 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full -mr-16 -mt-16" />
                  <h3 className="text-3xl text-white font-light mb-3">Predictive</h3>
                  <p className="text-white/80 font-light relative z-10">Route around congestion before it peaks</p>
                </div>
              </div>
            </div>

            {/* Replace with simpler Key Benefits */}
            <div className="mb-24">
              <h2 className="text-3xl md:text-4xl text-white font-light tracking-wider mb-6">
                The Real-Time Difference
              </h2>
              <div className="grid md:grid-cols-2 gap-12">
                <div className="bg-white/5 rounded-lg p-8">
                  <h3 className="text-xl text-white font-light tracking-wide mb-6">Everyone Else</h3>
                  <ul className="space-y-4">
                    <li className="flex items-center text-white/80">
                      <svg className="w-5 h-5 mr-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Minutes-old GPS data
                    </li>
                    <li className="flex items-center text-white/80">
                      <svg className="w-5 h-5 mr-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      React to traffic after it forms
                    </li>
                  </ul>
                </div>

                <div className="bg-white/5 rounded-lg p-8">
                  <h3 className="text-xl text-white font-light tracking-wide mb-6">Orchestra Phantom</h3>
                  <ul className="space-y-4">
                    <li className="flex items-center text-white/80">
                      <svg className="w-5 h-5 mr-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      True real-time traffic data
                    </li>
                    <li className="flex items-center text-white/80">
                      <svg className="w-5 h-5 mr-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Route around traffic before it peaks
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Applications Section */}
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-white/80 p-6 bg-white/5 rounded-lg">
                <h3 className="text-xl font-light tracking-wide mb-3">Urban Planning</h3>
                <p className="font-light">Optimize road infrastructure based on real traffic patterns.</p>
              </div>
              <div className="text-white/80 p-6 bg-white/5 rounded-lg">
                <h3 className="text-xl font-light tracking-wide mb-3">Traffic Management</h3>
                <p className="font-light">Real-time insights for dynamic traffic control systems.</p>
              </div>
              <div className="text-white/80 p-6 bg-white/5 rounded-lg">
                <h3 className="text-xl font-light tracking-wide mb-3">Emergency Response</h3>
                <p className="font-light">Faster incident detection and response coordination.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add PricingCTA before closing divs */}
      {/* <PricingCTA className="mb-24" /> */}
    </div>
  );
};

export default Phantom; 